import type * as zod from 'zod';

import type { ReferenceFrame } from '@sb/sb-robot-sdk/src/codegen/typescript-axios';

import CreateRecordingStep from '../CreateRecording/Step';
import type { StepKind } from '../StepKind';

import Arguments from './Arguments';
import Variables from './Variables';

// Identical to CreateRecording except the recorder runs in `demonstration`
// mode — echo-bot routes the recording to the demonstration_recordings table
// and skips the cloud-skill upload. All recorder orchestration is inherited.
export default class CreateDemonstrationStep extends CreateRecordingStep {
  public static Arguments = Arguments;

  public static Variables = Variables;

  protected getRecordingMode(): 'standard' | 'demonstration' {
    return 'demonstration';
  }

  // Capture the selected reference's world pose at record time as
  // `refWorldPose`, so replay can shift the demonstration to the same
  // reference selected again at runtime. Throws if `referenceRef` is set but
  // can't be resolved.
  protected override async getReferenceFrame(): Promise<
    ReferenceFrame | undefined
  > {
    const { referenceRef, referenceIndex, referenceWorldPose } = this
      .args as zod.infer<typeof Arguments>;

    // Pre-resolved reference (the wizard's live Locate result): use it as-is.
    // Like the environmentObject branch below it carries no tcpOption — the
    // pose is the referenced object's world frame, not a taught robot
    // target. Replay anchors in the frame of the reference it re-grounds
    // onto at runtime (the locate output entry, tagged 'ee-any').
    if (referenceWorldPose) {
      const { x, y, z, i, j, k, w } = referenceWorldPose;

      return { refWorldPose: { x, y, z, i, j, k, w } };
    }

    if (!referenceRef) return undefined;

    let space;

    try {
      space = this.routineFrame.getSpaceItem(referenceRef);
    } catch {
      space = undefined;
    }

    const index = referenceIndex ?? 0;

    // Position-list / Locate items expose poses via `positions[]`;
    // `environmentObject` items store a single `pose` with empty `positions[]`.
    let refPose;
    let refTcpOption: string | undefined;

    if (space && 'positions' in space && (space.positions?.length ?? 0) > 0) {
      // Resolve strictly by index — out-of-bounds must error, not fall through
      // to the world-frame `pose` branch.
      const entry = space.positions?.[index];

      if (!entry?.pose) {
        throw new Error(
          `Demonstration reference "${referenceRef}" has no pose at index ` +
            `${index} (position list has ${space.positions?.length ?? 0} entries)`,
        );
      }

      // Use raw (wrist frame). Demonstration recordings live in the wrist
      // frame, and replay also uses the target raw, so the matching gripper
      // offsets cancel in the shift.
      refPose = entry.pose;
      refTcpOption = entry.tcpOption;
    } else if (space && 'pose' in space) {
      // environmentObject: pose is the object's world frame; no tcpOption.
      refPose = space.pose;
    }

    if (!refPose) {
      throw new Error(
        `Demonstration reference "${referenceRef}" could not be resolved to a pose at index ${index}`,
      );
    }

    return {
      refWorldPose: {
        x: refPose.x,
        y: refPose.y,
        z: refPose.z,
        i: refPose.i,
        j: refPose.j,
        k: refPose.k,
        w: refPose.w,
      },
      ...(refTcpOption !== undefined ? { refTcpOption } : {}),
    };
  }

  // Base lookup scans StepRegistry by `instanceof` and returns the first
  // match — without this override a CreateDemonstration instance resolves to
  // 'CreateRecording', corrupting state + step-variable tags + AddTag checks.
  public override getStepKind(): StepKind {
    return 'CreateDemonstration';
  }
}
